import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Code2, FileText, Gauge, Globe, ListChecks, Network, ShieldCheck, Smartphone } from "lucide-react";
import "../../css/Home.css";
import { BetaBadge, Button, ScreenshotFrame } from "../common/ui";
import FrothAiIcon from "../common/FrothAiIcon";
import LandingHeadline from "../common/LandingHeadline";
import heroShot from "../../../assets/product/hero-defects.webp";
import jiraLogo from "../../../assets/icons/jira.png";

// The areas named in the hero lead, shown as one bar under the actions; the highlight steps through them.
const MODULES = [
  { label: "Requirements", Icon: FileText },
  { label: "Test management", Icon: ListChecks },
  { label: "Automation", Icon: Code2 },
  { label: "API testing", Icon: Network },
  { label: "Performance", Icon: Gauge },
  { label: "Security", Icon: ShieldCheck },
];

const STEP_MS = 2400;

function useCycle(length, delay) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setInterval(() => setIndex(i => (i + 1) % length), delay);
    return () => clearInterval(t);
  }, [length, delay]);
  return index;
}

const INTRO_KEY = "ftHeroIntroSeen";

// Brand intro reel (public/videos/hero-intro*): the Froth TestOps and FrothAI logos, then generate, execute
// and release. Played over the hero on a visitor's first landing only, then faded away. Portrait screens
// get the 1:2 cut.
// Skipped for reduced motion. It stays invisible until the video is actually playing, and is dropped if it
// can't start within a few seconds, so the hero is never covered by a blank panel.
function HeroIntro() {
  const [state, setState] = useState("off"); // off -> loading -> on -> hiding -> off
  const [portrait, setPortrait] = useState(false);
  const video = useRef(null);
  const hide = useCallback(() => setState(s => (s === "on" ? "hiding" : s === "loading" ? "off" : s)), []);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    try {
      if (localStorage.getItem(INTRO_KEY)) return; // only on the visitor's first landing
      localStorage.setItem(INTRO_KEY, "1");
    } catch {
      return; // storage blocked: we can't tell a first visit, so don't risk replaying it
    }
    setPortrait(window.matchMedia?.("(max-aspect-ratio: 4/5)").matches ?? false);
    setState("loading");
  }, []);

  useEffect(() => {
    if (state === "loading") {
      video.current?.play().catch(hide); // autoplay blocked
      const t = setTimeout(hide, 4000); // too slow to start: skip it
      return () => clearTimeout(t);
    }
    if (state === "on") {
      const t = setTimeout(hide, 20000); // never leave the hero covered
      return () => clearTimeout(t);
    }
    return undefined;
  }, [state, hide]);

  if (state === "off") return null;
  const src = portrait ? "/videos/hero-intro-mobile" : "/videos/hero-intro";

  return (
    <div
      className={`home-hero__intro is-${state}`}
      aria-hidden="true"
      onTransitionEnd={() => state === "hiding" && setState("off")}
    >
      <video
        ref={video}
        muted
        playsInline
        preload="auto"
        onPlaying={() => setState(s => (s === "loading" ? "on" : s))}
        onEnded={hide}
      >
        <source src={`${src}.webm`} type="video/webm" />
        <source src={`${src}.mp4`} type="video/mp4" onError={hide} />
      </video>
      <button type="button" className="home-hero__intro-skip" onClick={hide} tabIndex={-1}>
        Skip intro
      </button>
    </div>
  );
}

// Cards hanging off the screenshot's edges. Card text is real product UI copy
// (Ask FrothAI prompts, defect priority levels, script targets) and carries no made-up numbers.
function StageCards() {
  return (
    <div className="hero-stage__cards" aria-hidden="true">
      <div className="hero-card hero-card--ask">
        <div className="hero-card__head">
          <FrothAiIcon size={16} />
          Ask FrothAI
          <BetaBadge title="Ask FrothAI is in beta" />
        </div>
        <span className="hero-card__prompt">What are the coverage gaps?</span>
        <span className="hero-card__prompt">Which requirements have no test cases?</span>
      </div>

      <div className="hero-card hero-card--script">
        <div className="hero-card__head">
          <FrothAiIcon size={16} />
          Generate with FrothAI
        </div>
        <div className="hero-card__targets">
          <span className="is-on">
            <Globe size={13} /> Web <Check size={12} strokeWidth={3} />
          </span>
          <span>
            <Smartphone size={13} /> Android
          </span>
          <span>
            <Smartphone size={13} /> iOS
          </span>
        </div>
      </div>

      {/* Execution split of the FrothShop demo cycle (FSHOP-TCY-1) in the product. */}
      <div className="hero-card hero-card--cycle">
        <div className="hero-card__head">
          <ListChecks size={15} className="hero-card__cycle" />
          Test cycle · 69% passed
        </div>
        <span className="hero-card__meter">
          <i style={{ "--w": "69%", "--c": "#22c55e" }} />
          <i style={{ "--w": "19%", "--c": "#ef4444" }} />
          <i style={{ "--w": "12%", "--c": "#f59e0b" }} />
        </span>
        <span className="hero-card__legend">
          <b style={{ "--c": "#22c55e" }}>Passed</b>
          <b style={{ "--c": "#ef4444" }}>Failed</b>
          <b style={{ "--c": "#f59e0b" }}>Not run</b>
        </span>
      </div>

      <span className="hero-chip">
        <img src={jiraLogo} alt="" width="18" height="18" /> Two-way Jira sync
      </span>
    </div>
  );
}

// Home hero: minimal and centred, unlike the inner pages. A wide two-line headline, then a bar of the
// platform's areas with a moving highlight, then the product screenshot with cards on its edges.
export default function Banner() {
  const active = useCycle(MODULES.length, STEP_MS);

  return (
    <section className="home-hero">
      <HeroIntro />
      <div className="ft-container">
        <div className="home-hero__copy">
          <Link to="/test-labs" className="home-hero__news">
            <span className="home-hero__news-tag">New</span>
            AI automation scripts &amp; Test Labs
            <ArrowRight size={14} aria-hidden="true" />
          </Link>

          <LandingHeadline className="home-hero__title">
            Streamline your <span className="home-hero__accent">quality assurance</span>{" "}
            <br />
            with Froth TestOps
          </LandingHeadline>

          <p className="home-hero__lead">
            One platform for requirements, test management, automation, API, performance and security testing, with
            FrothAI to generate tests, scripts and release-readiness reports.
          </p>

          <div className="home-hero__actions">
            <Button to="/contact" icon>
              Talk to Us
            </Button>
            <Link to="/froth-ai" className="ft-btn home-hero__ghost">
              <FrothAiIcon size={18} /> Explore FrothAI
            </Link>
          </div>
        </div>

        {/* The QA lifecycle as a pipeline: the rail fills up to the active step, and the
            FrothAI layer underneath lights the dot under that step. */}
        <div
          className="hero-flow"
          style={{ "--step": `${STEP_MS}ms`, "--n": MODULES.length, "--active": active }}
        >
          <ol className="hero-flow__nodes" aria-label="Covered in one platform">
            {MODULES.map(({ label, Icon }, i) => (
              <li key={label} className={i === active ? "is-active" : i < active ? "is-done" : ""}>
                <span className="hero-flow__node">
                  <Icon size={18} aria-hidden="true" />
                </span>
                <span className="hero-flow__label">{label}</span>
              </li>
            ))}
          </ol>
          <div className="hero-flow__ai">
            <span className="hero-flow__shine" aria-hidden="true">
              <span className="hero-flow__beam" />
            </span>
            <span className="hero-flow__dots" aria-hidden="true">
              {MODULES.map(({ label }, i) => (
                <i key={label} className={i === active ? "is-on" : ""} />
              ))}
            </span>
            <span className="hero-flow__ai-text">
              <span className="hero-flow__ai-mark">
                <FrothAiIcon size={14} />
              </span>
              <strong>FrothAI</strong> works across every step
            </span>
          </div>
        </div>

        <div className="hero-stage">
          <span className="hero-stage__halo" aria-hidden="true" />
          <ScreenshotFrame
            className="home-hero__shot"
            src={heroShot}
            height={563}
            priority
            alt="Froth TestOps defect management with priority and severity breakdown for the FrothShop Web Store project"
          />
          <StageCards />
        </div>
      </div>
    </section>
  );
}
